import type { GaApp } from '~/modules/app'
import type { InitialModalQueueFlags } from '~/modules/main'

/**
 * Повторный запрос при сетевых ошибках
 */
export async function retryWithDelay<T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 1500,
): Promise<T> {
  try {
    return await fn()
  } catch (error: unknown) {
    const isErrorObject = typeof error === 'object' && error !== null
    const statusCode =
      isErrorObject && 'statusCode' in error
        ? Number((error as { statusCode?: number }).statusCode)
        : Number.NaN
    const isClientError = statusCode >= 400 && statusCode < 500
    const isNetworkError =
      isErrorObject &&
      ('message' in error || 'status' in error) &&
      !isClientError

    // Если попытки остались и это сетевая ошибка (не 401/403/404)
    if (retries > 0 && isNetworkError) {
      await new Promise((resolve) => setTimeout(resolve, delay))
      return retryWithDelay(fn, retries - 1, delay * 1.5)
    }
    throw error
  }
}

export class MainService {
  gaApp: GaApp
  private catchPrizeInFlight = false

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }

  get spinErrorText() {
    return this.gaApp.i18n.t('main.roulette.spinError')
  }

  get needShowSplash() {
    return this.gaApp.stores.main.main.needShowSplash
  }

  /**
   * Подготовка данных ежедневной награды (без открытия модалки).
   * Открытие — через ModalQueue или openDailyRewardModal.
   */
  prepareDailyReward(days: number) {
    this.gaApp.services.main.user.disableGetUserTimer()

    const { dailyRewards } = this.gaApp.stores.main.main.gameConfiguration

    const sortedDays = [...dailyRewards].sort(
      (a, b) => a.dayNumber - b.dayNumber,
    )

    this.gaApp.stores.main.modals.dailyReward.data = sortedDays.map(
      ({ dayNumber, reward }) => ({
        id: dayNumber,
        name: String(dayNumber),
        coins: reward,
        received: dayNumber <= days,
        claimed: dayNumber < days,
      }),
    )
  }

  /**
   * Обработчик ежедневной награды
   * вызывается при запросе юзера.
   */
  /**
   * Метод для правильного склонения слова "бьютисы" в зависимости от числа
   */
  getBeautyWord(coins: number): string {
    const lastDigit = coins % 10
    const lastTwoDigits = coins % 100

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14)
      return this.gaApp.i18n.t('main.dailyRewardModal.pluralCoins')
    if (lastDigit === 1)
      return this.gaApp.i18n.t('main.dailyRewardModal.nominativeCoin')
    if (lastDigit >= 2 && lastDigit <= 4)
      return this.gaApp.i18n.t('main.dailyRewardModal.genitiveCoin')

    return this.gaApp.i18n.t('main.dailyRewardModal.pluralCoins')
  }

  /**
   * Получение данных о конфигурации игры
   */
  async getGameConfiguration() {
    try {
      return await this.gaApp.services.main.api.getGameConfiguration()
    } catch (error) {
      // TODO Необходимо мподумать о дефолтных данных (учитывая мультиязычность)
      return {}
    }
  }

  /**
   * Получение ежедневной награды по клику на "забрать награду".
   * Запрашиваем юзера, в нем получаем обновленные coins (с учетом награды),
   * флаг showDailyReward становится false.
   */
  async claimReward(coins: number) {
    try {
      const response =
        await this.gaApp.services.main.api.updateDailyModalState()

      if (response?.statusCode === 200) {
        const beautyWord = this.getBeautyWord(coins)
        const icon =
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.8268 10.238H12.7909C14.1984 10.238 15.5905 11.4016 15.3394 12.7865C15.2557 13.2481 14.8591 13.9324 14.3979 14.3935C13.9367 14.8547 13.2524 15.2514 12.7908 15.3351C11.4059 15.5861 10.2423 14.194 10.2423 12.7865V11.8268H10.238C10.238 11.6953 10.1315 11.5888 10 11.5888C9.86854 11.5888 9.762 11.6953 9.762 11.8268H9.7577V12.7865C9.7577 14.194 8.5941 15.5861 7.20917 15.3351C6.74759 15.2514 6.06334 14.8547 5.60216 14.3935C5.14094 13.9324 4.74429 13.2481 4.66063 12.7865C4.40953 11.4016 5.80163 10.238 7.20913 10.238H8.17322C8.30466 10.238 8.41124 10.1315 8.41124 10C8.41124 9.86858 8.30466 9.762 8.17322 9.762H7.20913C5.80163 9.762 4.40953 8.5984 4.66063 7.21347C4.74429 6.75189 5.14094 6.06765 5.60216 5.60646C6.06334 5.14527 6.74759 4.74863 7.20917 4.66494C8.5941 4.41387 9.7577 5.80596 9.7577 7.21347V8.17322H9.762C9.762 8.30466 9.86854 8.41124 10 8.41124C10.1315 8.41124 10.238 8.30466 10.238 8.17322H10.2423V7.21347C10.2423 5.80596 11.4059 4.41387 12.7908 4.66494C13.2524 4.74863 13.9367 5.14527 14.3979 5.60646C14.8591 6.06765 15.2557 6.75189 15.3394 7.21347C15.5905 8.5984 14.1984 9.762 12.7909 9.762H11.8268C11.6953 9.762 11.5888 9.86858 11.5888 10C11.5888 10.1315 11.6953 10.238 11.8268 10.238ZM10 0C4.47718 0 0 4.47718 0 9.99998C0 15.5229 4.47718 20 10 20C15.5229 20 20 15.5229 20 9.99998C20 4.47718 15.5229 0 10 0Z" fill="white"/></svg>'
        let slotText = `<div style="display: flex; align-items: center; white-space: nowrap;">${icon} <div style="font-weight: 500; margin-left: 0.7rem; margin-right: 0.3rem">${coins} ${beautyWord} </div> ${this.gaApp.i18n.t('main.dailyRewardModal.claimNotification')}</div>`

        if (coins === 0) {
          slotText = `<div style="display: flex; align-items: center; white-space: nowrap;"> ${this.gaApp.i18n.t('main.dailyRewardModal.saveProgressNotify')}</div>`
        }

        this.gaApp.services.notification.main.open({
          slotText,
        })
      }

      this.gaApp.services.main.user.enableGetUserTimer()
      await this.gaApp.services.main.user.getUser()
    } catch (error) {
      this.gaApp.services.app.rejection.log({
        name: 'main:claim-reward',
        message: 'Ошибка получения ежедневной награды',
        errorData: error,
        userNotification: this.spinErrorText,
      })
    } finally {
      this.gaApp.services.main.modal.closeDailyRewardModal()
    }
  }

  /**
   * Прокрутка рулетки по клику на "получить приз".
   * Запрашиваем юзера, в нем получаем обновленные coins.
   */
  async catchPrize() {
    if (this.catchPrizeInFlight) return null

    const canSpin = this.gaApp.stores.main.user.canSpin

    if (!canSpin) return null

    this.catchPrizeInFlight = true

    try {
      const prizeId = await this.gaApp.services.main.api.catchPrize()

      if (prizeId === null) {
        this.gaApp.services.notification.main.open(
          this.gaApp.i18n.t('main.roulette.spinError'),
        )

        return null
      }

      await this.gaApp.services.main.user.getUser()

      // ищем приз среди призов пользователя
      const prize =
        (prizeId &&
          this.gaApp.stores.main.user.data?.prizes?.find?.(
            (prize) => prize.id === prizeId,
          )) ||
        null

      this.gaApp.services.main.modal.setPrizeData(prize)

      if (!prize) {
        this.gaApp.services.app.rejection.log({
          name: 'main:spin-wheel-empty-prize',
          message: 'Ошибка запуска прокрутки рулетки - не нашли приз',
          errorData: { prizeId },
          userNotification: this.spinErrorText,
        })

        return null
      }

      return prize.prizeDescription.id
    } catch {
      this.gaApp.services.notification.main.open(
        this.gaApp.i18n.t('main.roulette.spinError'),
      )

      return null
    } finally {
      this.catchPrizeInFlight = false
    }
  }

  /** Меняет флаг открытия сплеша */
  closeSplash() {
    this.gaApp.stores.main.main.needShowSplash = false
  }

  /**
   * Обработка модалок и стартового экрана после GET /user
   */
  handleInitialUserModals(
    flags: InitialModalQueueFlags,
    silent?: boolean,
  ): void {
    if (
      silent === true ||
      this.gaApp.stores.main.user.hasHandledInitialUserModals === true
    ) {
      return
    }

    this.gaApp.stores.main.user.hasHandledInitialUserModals = true

    this.gaApp.services.main.modalQueue.startInitial(flags)
  }
}
